import {memo, useEffect, useState} from 'react'
import {useRegisterSW} from 'virtual:pwa-register/react'

import {APP_VERSION} from '../config'
import {notificationNewReleaseEventName} from '../enums/event'
import {useMeta} from '../hooks/entity/useMeta'
import {useEvent} from '../hooks/useEvent'
import eventDispatcher from '../utils/eventDispatcher'

const updateInterval = 5 * 60 * 1000

// Cooldown key shared with lazyWithReload, so the two recovery layers don't double-trigger.
const RECOVERY_KEY = 'pwa:lazy-reload-at'
const RECOVERY_GUARD_MS = 60_000

function recoveredRecently(): boolean {
  try {
    const raw = localStorage.getItem(RECOVERY_KEY)
    if (raw === null) return false
    const at = Number(raw)
    return Number.isFinite(at) && (Date.now() - at) < RECOVERY_GUARD_MS
  } catch {
    return false
  }
}

function isBrokenRegistrationError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return msg.includes('newestWorker is null')
    || msg.includes('Cannot update a null/nonexistent service worker registration')
}

let recovering = false
async function recoverFromBrokenSw(): Promise<void> {
  if (recovering || recoveredRecently()) return
  recovering = true
  try {
    localStorage.setItem(RECOVERY_KEY, String(Date.now()))
  } catch {
    // localStorage may be unavailable — proceed anyway
  }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
    const regs = await navigator.serviceWorker?.getRegistrations?.() ?? []
    await Promise.all(regs.map((r) => r.unregister()))
  } catch {
    // best-effort cleanup
  }
  window.location.reload()
}

export const ReleaseHandler = memo(function ReleaseHandler() {
  const {meta} = useMeta(APP_VERSION !== 'dev')
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | undefined>()
  const [postponeUpdate, setPostponeUpdate] = useState(false)

  useRegisterSW({
    onRegisteredSW(_scriptUrl, registration) {
      setSwRegistration(registration)
      // The browser only auto-refetches sw.js once every 24h when an SW is already cached for the origin.
      registration?.update().catch((err: unknown) => {
        if (isBrokenRegistrationError(err)) {
          void recoverFromBrokenSw()
        }
      })
    },
  })

  const updateSw = useEvent(async () => {
    if (!swRegistration) {
      setPostponeUpdate(true)
      return
    }
    try {
      await swRegistration.update()
    } catch (err) {
      if (isBrokenRegistrationError(err)) {
        void recoverFromBrokenSw()
      }
      // any other rejection — next interval will retry
    }
  })

  useEffect(() => {
    if (swRegistration && postponeUpdate) {
      setPostponeUpdate(false)
      updateSw()
    }
  }, [postponeUpdate, swRegistration, updateSw])

  useEffect(() => {
    const interval = setInterval(updateSw, updateInterval)
    return () => clearInterval(interval)
  }, [updateSw])

  useEffect(() => {
    const controller = new AbortController()
    eventDispatcher.addEventListener(notificationNewReleaseEventName, () => {
      updateSw()
    }, {signal: controller.signal})
    return () => controller.abort()
  }, [updateSw])

  useEffect(() => {
    if (!meta || APP_VERSION === 'dev') return
    if (meta.clientVersion > APP_VERSION) {
      updateSw()
    }
  }, [meta, updateSw])

  return null
})
